import React from 'react';
import { Coffee, Flame, Scissors, Sparkles, Award, Star, CheckCircle2, Shield, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function AuthenticRituals({ onOpenBooking }) {
  const { lang } = useLanguage();

  const rituals = [
    {
      step: "01",
      icon: Coffee,
      color: "from-amber-500 to-amber-700",
      border: "border-amber-400/40",
      bgGlow: "bg-amber-500/10",
      accent: "text-amber-600",
      pillBg: "bg-amber-100 text-amber-900",
      title: {
        nl: "Warm Welkom & Italiaanse Espresso",
        en: "Warm Welcome & Italian Espresso",
        tr: "Sıcak Karşılama & İtalyan Espresso"
      },
      desc: {
        nl: "Neem plaats in onze comfortabele lederen zetels, geniet van verse barista koffie of frisdrank en ervaar rustgevende lounge muziek.",
        en: "Take a seat in our luxury leather chairs, enjoy fresh artisan coffee, and unwind to relaxing lounge beats.",
        tr: "Hakiki deri berber koltuklarımızda yerinizi alın, taze çekilmiş barista kahveniz eşliğinde dinlendirici atmosferin tadını çıkarın."
      }
    },
    {
      step: "02",
      icon: Flame,
      color: "from-emerald-500 to-teal-700",
      border: "border-emerald-400/40",
      bgGlow: "bg-emerald-500/10",
      accent: "text-emerald-600",
      pillBg: "bg-emerald-100 text-emerald-900",
      title: {
        nl: "Traditionele Warme Handdoek & Stoom",
        en: "Traditional Hot Towel & Herbal Steam",
        tr: "Geleneksel Sıcak Havlu & Bitkisel Buhar"
      },
      desc: {
        nl: "Essentiële eucalyptus oliën en warme stomende handdoeken openen de poriën en ontspannen de gezichtsspieren voor ultiem comfort.",
        en: "Herbal eucalyptus essential oils and steaming hot towels open pores and relax facial tension for pure rejuvenation.",
        tr: "Okaliptüs özlü sıcak aromaterapik havlu ve buhar ritüeliyle gözenekler açılır, cildiniz ve yüz kaslarınız derinlemesine gevşer."
      }
    },
    {
      step: "03",
      icon: Scissors,
      color: "from-rose-500 to-red-700",
      border: "border-rose-400/40",
      bgGlow: "bg-rose-500/10",
      accent: "text-rose-600",
      pillBg: "bg-rose-100 text-rose-900",
      title: {
        nl: "Klassieke Open Mes & Baard Sculptuur",
        en: "Straight Razor Mastery & Beard Sculpting",
        tr: "Geleneksel Ustura & Sakal Heykeltıraşlığı"
      },
      desc: {
        nl: "Vakmanschap met het klassieke open scheermes, rijke Italiaanse scheerschuim en vlijmscherpe baardcontouren.",
        en: "Master straight razor shaving with rich traditional lather for laser-sharp contours and baby-smooth skin.",
        tr: "Zengin İtalyan tıraş köpüğü ve usta ellerden çıkan klasik çelik ustura dokunuşlarıyla kusursuz ve sinekkaydı hatlar."
      }
    },
    {
      step: "04",
      icon: Sparkles,
      color: "from-indigo-500 to-purple-700",
      border: "border-indigo-400/40",
      bgGlow: "bg-indigo-500/10",
      accent: "text-indigo-600",
      pillBg: "bg-indigo-100 text-indigo-900",
      title: {
        nl: "Meister Knipkunst & Haarwerk Perfectie",
        en: "Master Styling & Hairpiece Integration",
        tr: "Usta Saç Şekillendirme & Doğal Serum Bakımı"
      },
      desc: {
        nl: "Haarfijne skin fade overloop, styling met premium matte pomades en optioneel naadloze 100% echt haarwerk integratie.",
        en: "Precision scissor craft, seamless fade transitions, premium organic pomades, and bespoke hairpiece styling.",
        tr: "Milisaniyelik milimetrik fade geçişleri, organik argan yağları, mat pomadlar ve isteğe bağlı %100 doğal protez saç uyumu."
      }
    }
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-stone-900 via-slate-900 to-stone-950 text-white relative overflow-hidden">
      
      {/* 🌌 Vibrant Background Ambient Lighting */}
      <div className="absolute top-10 left-10 w-96 h-96 rounded-full glow-aurora-amber pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full glow-aurora-emerald pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-80 rounded-full glow-aurora-ruby pointer-events-none" />

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Title Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-xs font-black uppercase tracking-widest shadow-lg">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span>{lang === 'nl' ? 'DE AURA HAIR ERVARING' : lang === 'tr' ? 'OTANTİK BERBER RİTÜELİ' : 'THE ARTISANAL EXPERIENCE'}</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-black text-white tracking-tight">
            {lang === 'nl' ? (
              <>Sfeer, Vakmanschap & <span className="text-shimmer-gold">Puur Ritueel</span></>
            ) : lang === 'tr' ? (
              <>Sadece Bir Kesim Değil, <span className="text-shimmer-gold">Ayrıcalıklı Bir Ritüel</span></>
            ) : (
              <>More than a Cut, <span className="text-shimmer-gold">A Pure Ritual</span></>
            )}
          </h2>

          <p className="text-sm sm:text-base text-stone-300 font-medium leading-relaxed">
            {lang === 'nl' 
              ? 'Bij Aura Hair Rotterdam draait alles om aandacht, ontspanning en ambacht. Elke behandeling is een complete verwennerij voor de moderne man.'
              : lang === 'tr'
              ? 'Aura Hair Rotterdam\'da her koltuk randevusu; sıcak havludan İtalyan kahvesine, ustura sanatından saç bakımına kadar eksiksiz bir erkek bakım deneyimidir.'
              : 'At Aura Hair Rotterdam, every visit is a bespoke grooming ritual tailored to perfection.'}
          </p>
        </div>

        {/* 4 Ritual Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {rituals.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div 
                key={idx}
                className={`p-6 sm:p-7 rounded-3xl bg-stone-800/80 backdrop-blur-xl border ${item.border} hover:scale-105 transition-all duration-300 relative overflow-hidden group shadow-xl flex flex-col justify-between`}
              >
                {/* Step Number Watermark in Background */}
                <div className="absolute top-2 right-4 text-5xl sm:text-6xl font-black font-mono text-white/5 group-hover:text-white/10 transition-colors pointer-events-none">
                  {item.step}
                </div>

                <div>
                  {/* Icon & Step Pill */}
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center shadow-lg shadow-black/40 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 stroke-[2.2]" />
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-black uppercase font-mono ${item.pillBg}`}>
                      {lang === 'nl' ? 'Stap' : lang === 'tr' ? 'Adım' : 'Step'} {item.step}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-serif text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-amber-300 transition-colors">
                    {item.title[lang] || item.title.nl}
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-stone-300 font-medium leading-relaxed">
                    {item.desc[lang] || item.desc.nl}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-stone-700/60 flex items-center justify-between text-xs text-stone-400 font-semibold">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>{lang === 'nl' ? 'Inclusief' : lang === 'tr' ? 'Dahil' : 'Included'}</span>
                  </span>
                  <span className="text-amber-400 font-bold font-mono">100% Aura Luxe</span>
                </div>

              </div>
            );
          })}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-amber-600/30 via-stone-800/80 to-emerald-600/30 border border-amber-400/40 backdrop-blur-xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-2xl">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="font-serif text-xl sm:text-2xl font-black text-white">
              {lang === 'nl' ? 'Klaar voor de Ultieme Verzorging?' : lang === 'tr' ? 'Kendinizi Şımartmaya Hazır Mısınız?' : 'Ready for the Ultimate Experience?'}
            </h4>
            <p className="text-xs sm:text-sm text-stone-300 font-medium">
              {lang === 'nl' 
                ? 'Kies uw behandeling en ervaar het verschil aan het Samuel Esmeijerplein 3.' 
                : lang === 'tr' 
                ? 'Randevunuzu hemen oluşturun, beklemeden koltuğunuza geçin.' 
                : 'Book your bespoke appointment and experience the craft at Samuel Esmeijerplein 3.'}
            </p>
          </div>

          <button
            onClick={onOpenBooking}
            className="btn-gold-sheen px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-black text-xs sm:text-sm uppercase tracking-wider shadow-xl shadow-amber-500/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shrink-0"
          >
            <span>{lang === 'nl' ? 'Direct Afspraak Maken' : lang === 'tr' ? 'Hemen Randevu Al' : 'Book Appointment Now'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </div>
    </section>
  );
}
